/**
 * Header Component
 * 
 * Contains logo, search bar, and collapsible filters
 * Class component for Chrome 68 compatibility
 */

import React, { Component } from 'react';
import HeaderFilters from './HeaderFilters';

class Header extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filtersOpen: false,
            searchValue: props.searchQuery || ''
        };
        this.toggleFilters = this.toggleFilters.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
        this.handleSearchButton = this.handleSearchButton.bind(this);
        this.handleClearSearch = this.handleClearSearch.bind(this);
        this.debounceTimeout = null;
        this.wrapperRef = null;
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }

    componentDidMount() {
        if (typeof document !== 'undefined') {
            document.addEventListener('click', this.handleDocumentClick, true);
        }
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined') {
            document.removeEventListener('click', this.handleDocumentClick, true);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.searchQuery !== this.props.searchQuery) {
            this.setState({ searchValue: this.props.searchQuery || '' });
        }
    }
    
    toggleFilters() {
        this.setState({ filtersOpen: !this.state.filtersOpen });
    }
    
    // Check if any filters are active
    hasActiveFilters() {
        var props = this.props;
        return (props.selectedCategories && props.selectedCategories.length > 0) ||
               (props.selectedSource && props.selectedSource !== 'all') ||
               (props.selectedVendor && props.selectedVendor !== '') ||
               (props.activeSorts && props.activeSorts.length > 0) ||
               (this.state.searchValue && this.state.searchValue !== '');
    }
    
    handleSearchChange(event) {
        var value = event.target.value;
        var self = this;
        
        this.setState({ searchValue: value });
        
        // On pages where filters are hidden (profile/vote), don't auto-search on input
        if (this.props.hideFilters) {
            return;
        }

        // Debounce search
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
        
        this.debounceTimeout = setTimeout(function() {
            if (self.props.onSearch) {
                self.props.onSearch(value);
            }
        }, 300);
    }
    
    handleSearchSubmit(event) {
        event.preventDefault();
        if (this.props.onSearch) {
            this.props.onSearch(this.state.searchValue);
        }
    }

    handleSearchButton(event) {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        var query = (this.state.searchValue || '').trim();
        if (query) {
            if (this.props.onSearch) {
                this.props.onSearch(query);
            }
        } else if (this.searchInputRef && this.searchInputRef.focus) {
            try { this.searchInputRef.focus(); } catch (e) {}
        }
    }

    handleClearSearch(event) {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        var self = this;
        this.setState({ searchValue: '' }, function() {
            if (self.props.onSearch) {
                self.props.onSearch('');
            }
            if (self.searchInputRef && self.searchInputRef.focus) {
                try { self.searchInputRef.focus(); } catch (e) {}
            }
        });
    }

    handleDocumentClick(event) {
        if (!this.state.filtersOpen) return;
        if (!this.wrapperRef) return;
        var target = event.target;
        var el = this.wrapperRef;
        if (el && target && el.contains) {
            var inside = el.contains(target);
            if (!inside) {
                this.setState({ filtersOpen: false });
            }
        }
    }
    
    render() {
        var props = this.props;
        var state = this.state;
        var self = this;
        var hasActiveFilters = this.hasActiveFilters();
        
        // Filter toggle class - orange when filters active and collapsed
        var filterToggleClass = 'bb-filter-toggle';
        if (state.filtersOpen) {
            filterToggleClass += ' active';
        } else if (hasActiveFilters) {
            filterToggleClass += ' has-filters';
        }
        
        return React.createElement('header', { className: 'bb-header' },
            React.createElement('div', { className: 'container', ref: function(el){ self.wrapperRef = el; } },
                // Top row: Logo + Login / Profile chip
                React.createElement('div', { className: 'bb-header-top' },
                    React.createElement('div', { className: 'bb-header-brand' },
                        React.createElement('a', { href: '/', className: 'bb-logo' },
                            React.createElement('img', { src: '/favicon/favicon.svg', alt: '📱', className: 'bb-logo-img' }),
                            React.createElement('span', { className: 'bb-logo-text' }, '4.3AppStore')
                        ),
                        React.createElement('span', { className: 'bb-subtitle d-none d-md-inline ml-2' },
                            '— Android 4.3 apps for BlackBerry OS 10 and other legacy devices'
                        )
                    ),
                    React.createElement('div', { className: 'd-flex align-items-center' },
                        (props.totalApps > 0) ? React.createElement('span', { className: 'bb-app-count mr-3 d-sm-inline' },
                            React.createElement('strong', null, props.totalApps),
                            ' apps'
                        ) : null,
                        props.user
                            ? React.createElement('button', {
                                className: 'bb-user-chip',
                                onClick: this.props.onProfile
                              },
                                props.user.avatar_url
                                    ? React.createElement('img', { src: props.user.avatar_url, alt: props.user.username })
                                    : React.createElement('span', { className: 'bb-user-placeholder' }, (props.user.username || '?').charAt(0)),
                                React.createElement('span', { className: 'bb-user-name' }, '@' + (props.user.username || 'discorduser'))
                              )
                            : React.createElement('button', {
                                className: 'bb-btn bb-btn-primary bb-btn-sm',
                                onClick: this.props.onLogin
                              }, 'Login')
                    )
                ),
                
                // Search row
                React.createElement('div', { className: 'bb-header-search', ref: function(el){ self.wrapperRef = el; } },
                    React.createElement('form', { onSubmit: this.handleSearchSubmit, className: 'bb-search-form' },
                        React.createElement('div', { className: 'bb-search-wrapper' },
                            React.createElement('input', {
                                type: 'text',
                                className: 'bb-search-input',
                                placeholder: 'Search apps...',
                                value: state.searchValue,
                                onChange: this.handleSearchChange,
                                ref: function(el) { self.searchInputRef = el; }
                            }),
                            React.createElement('button', {
                                type: state.searchValue && state.searchValue.trim() ? 'button' : 'submit',
                                className: 'bb-search-btn',
                                onClick: state.searchValue && state.searchValue.trim() ? this.handleClearSearch : null
                            }, state.searchValue && state.searchValue.trim() ? '✕' : '🔍')
                        ),
                        !props.hideFilters ? React.createElement('button', {
                            type: 'button',
                            className: filterToggleClass,
                            onClick: this.toggleFilters
                        },
                            React.createElement('svg', {
                                className: 'bb-filter-icon',
                                'aria-hidden': 'true',
                                width: '16',
                                height: '16',
                                viewBox: '0 0 16 16',
                                focusable: 'false',
                                xmlns: 'http://www.w3.org/2000/svg'
                            },
                                React.createElement('path', {
                                    d: 'M2 4h12v2H2zm0 4h12v2H2zm0 4h12v2H2z',
                                    fill: 'currentColor'
                                })
                            ),
                            React.createElement('span', { className: 'sr-only' }, 'Filters'),
                            React.createElement('span', { className: 'd-none d-sm-inline ml-1' }, 'Filters'),
                            state.filtersOpen 
                                ? React.createElement('span', { className: 'ml-1' }, '▲')
                                : React.createElement('span', { className: 'ml-1' }, '▼')
                        ) : React.createElement('button', {
                            type: 'button',
                            className: filterToggleClass,
                            onClick: this.handleSearchButton
                        }, 'Search')
                    )
                ),
                
                !props.hideFilters && state.filtersOpen && React.createElement(HeaderFilters, {
                    categories: props.categories || [],
                    sources: props.sources || [],
                    selectedCategories: props.selectedCategories || [],
                    selectedSource: props.selectedSource || 'all',
                    activeSorts: props.activeSorts || [],
                    onCategoryChange: props.onCategoryChange,
                    onSourceChange: props.onSourceChange,
                    onSortChange: props.onSortChange
                })
            )
        );
    }
}

export default Header;
